import React, { useEffect, useState } from "react"
import { Alert, Button, Collapse, IconButton } from "@mui/material"
import "./GeneradorQRparaModal.css"
import { useSpinner } from "../../../../contexts/SpinnerProvider"
import { encryptText } from "../../../../utils/claveAndEncryptUtils"
import { handleRequest } from "../../../FuncionRequest/FuncionRequest"
import jsPDF from "jspdf"
import { toPng } from "html-to-image"
import { QrData, QR_CONFIGS } from "../../../GeneradorQR/qrConfigs"
import GenericQRCard from "../../../GeneradorQR/GenericQRCard"
import FiestaQRCard from "../../../GeneradorQR/FiestaQRCard"

interface QrValidationResponse {
  message: string
  nombre: string
  apellido: string
  tarjetasPagadas: string
  tarjetasPagadasMenores: string
  fechaEvento: string
  tipoEvento?: string
  categoria?: string
  status?: number
}

interface GeneradorQRparaModalProps {
  dni?: number | null
  clave?: string
  id?: number
  tipoEvento?: string
  categoria?: string
}

const GeneradorQRparaModal: React.FC<GeneradorQRparaModalProps> = ({
  dni: propDni,
  clave: propClave,
  id: propId,
  tipoEvento,
  categoria,
}) => {
  const dni = propDni || ""
  const clave = propClave || ""
  const id = propId || 0
  const isEgresadosCena = tipoEvento === "EGRESADOS" && categoria === "CENA"
  const [isValid, setIsValid] = useState(false)
  const [qrDataObj, setQrDataObj] = useState<QrData | null>(null)
  const [mensajeAlert, setMensajeAlert] = useState<React.ReactNode>(undefined)
  const [tipoAlert, setTipoAlert] = useState<"error" | "warning">("error")
  const { addLoading, removeLoading } = useSpinner()
  const API_URL = isEgresadosCena
    ? (process.env.REACT_APP_API_SERVER_URL_VALID || "")
    : (process.env.REACT_APP_API_SERVER_URL_VALID_ID || "")

  const handleMensajeAlert = (
    mensaje: React.ReactNode = undefined,
    tipo: "error" | "warning" = "error",
  ) => {
    setMensajeAlert(mensaje)
    setTipoAlert(tipo)
  }

  const handleGenerateQR = async () => {
    try {
      const qrBase = isEgresadosCena
        ? `${dni}-${encryptText(clave)}`
        : `i${id}-${encryptText(clave)}`
      const requestBody = isEgresadosCena
        ? { dni, clave }
        : { id, clave }
      const response = await handleRequest<QrValidationResponse>(
        "POST",
        API_URL,
        requestBody,
      )
      if (!response) {
        handleMensajeAlert("Error al conectar con el servidor.", "error")
        setIsValid(false)
        return
      }

      if (response.message === "sin tarjetas") {
        handleMensajeAlert(
          <div>
            <span style={{ display: "inline-block" }}>
              No posee tarjetas para generar QR,{" "}
            </span>
            <span style={{ display: "inline-block" }}>
              comuníquese con administración.
            </span>
          </div>,
        )
        setIsValid(false)
        return
      }

      if (response.message === "Plazo expirado") {
        handleMensajeAlert(
          "La fecha de generación de QR para este evento ha expirado.",
        )
        setIsValid(false)
        return
      }

      if (response.message === "Validado correctamente") {
        handleMensajeAlert()
        setQrDataObj({
          qrBase,
          nombre: response.nombre,
          apellido: response.apellido,
          fechaEvento: response.fechaEvento,
          tarjetasPagadas: parseInt(response.tarjetasPagadas) || 0,
          tarjetasPagadasMenores:
            parseInt(response.tarjetasPagadasMenores) || 0,
          tipoEvento: response.tipoEvento,
          categoria: response.categoria,
          dni: propDni || undefined,
        })
        setIsValid(true)
      } else if (response.status === 400) {
        setIsValid(false)
        handleMensajeAlert("DNI o clave incorrectos.", "warning")
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error de validación:", err)
      setIsValid(false)
      handleMensajeAlert(
        "Error de credenciales o al recuperar los datos en el servidor.",
        "error",
      )
    }
  }

  const handleDownloadPDF = async (): Promise<void> => {
    const contentElement = document.getElementById("printable-area")
    if (!contentElement || !qrDataObj) return

    try {
      addLoading()
      const dataUrl = await toPng(contentElement, {
        quality: 1,
        pixelRatio: 4,
        backgroundColor: "#ffffff",
      })
      if (!dataUrl) return

      const pdf = new jsPDF("p", "pt", "a4")
      const pdfW = pdf.internal.pageSize.width
      const pdfH = pdf.internal.pageSize.height
      const margin = 50
      const maxW = (pdfW - margin * 2) * 0.75
      const maxH = (pdfH - margin * 2) * 0.75
      const aspect = contentElement.clientHeight / contentElement.clientWidth
      const imgWidth = aspect * maxW > maxH ? maxH / aspect : maxW
      const imgHeight = imgWidth * aspect
      const x = (pdfW - imgWidth) / 2
      const y = 85

      pdf.setFillColor(255, 255, 255)
      pdf.rect(
        0,
        0,
        pdf.internal.pageSize.width,
        pdf.internal.pageSize.height,
        "F",
      )
      pdf.addImage(dataUrl, "PNG", x, y, imgWidth, imgHeight)
      pdf.save(`QR_${qrDataObj.apellido}_${qrDataObj.nombre}.pdf`)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error al generar el PDF:", error)
    } finally {
      removeLoading()
    }
  }

  useEffect(() => {
    handleGenerateQR()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const visibleConfigs = qrDataObj
    ? QR_CONFIGS.filter((c) => c.visibleSi(qrDataObj))
    : []

  return (
    <div>
      <div className="container text-center">
        <div
          style={{ maxWidth: "500px", margin: "auto", marginBottom: "50px" }}
        >
          {!!mensajeAlert && (
            <Collapse in={!!mensajeAlert}>
              <Alert
                severity={tipoAlert}
                variant="filled"
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => handleMensajeAlert()}
                  />
                }
                sx={{ textAlign: "center" }}
              >
                {mensajeAlert}
              </Alert>
            </Collapse>
          )}

          {isValid && qrDataObj && (
            <div className="qr-code text-center">
              <div id="printable-area" className="qr-code-container">
                {visibleConfigs.map((config) =>
                  config.cardTemplate === "fiesta" ? (
                    <FiestaQRCard
                      key={config.id}
                      config={config}
                      data={qrDataObj}
                    />
                  ) : (
                    <GenericQRCard
                      key={config.id}
                      config={config}
                      data={qrDataObj}
                      size={190}
                    />
                  ),
                )}
              </div>
              <div className="d-flex flex-row justify-content-center gap-5">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleDownloadPDF}
                >
                  Descargar PDF
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default GeneradorQRparaModal
